<template>
    <div class="doc-page">
        <div class="doc-toolbar no-print">
            <el-button icon="el-icon-arrow-left" @click="goBack">Back</el-button>
            <el-button type="primary" @click="exportPDF" size="medium">Export PDF</el-button>
        </div>

        <div id="invoiceReceiptPrint" class="wrap" v-loading="loading" v-if="applicationInfo">
            <!-- ============ INVOICE ============ -->
            <section class="sheet" v-if="documentMode !== 'receipt'">
                <div class="top">
                    <div class="seller">
                        <div class="name">EXTRACURRICULAR ACTIVITY EXCELLENCE INTERNATIONAL PTE. LTD.</div>
                        <div>60 PAYA LEBAR ROAD, #06-28 PAYA LEBAR SQUARE, SINGAPORE 409051</div>
                        <div>UEN: 202405060C</div>
                        <div>Email: contact@eae.international</div>
                    </div>
                    <div class="meta">
                        <table>
                            <tr><td class="k">INVOICE NO.</td><td class="v">{{ invoiceNo }}</td></tr>
                            <tr><td class="k">ISSUE DATE</td><td class="v">{{ issueDate }}</td></tr>
                            <tr><td class="k">DUE DATE</td><td class="v">{{ dueDate }}</td></tr>
                            <tr><td class="k">CURRENCY</td><td class="v">{{ currency }}</td></tr>
                        </table>
                    </div>
                </div>

                <div class="billto">
                    <div class="lbl">Bill To</div>
                    <div><b>{{ billToName }}</b></div>
                    <div v-if="billToCountry">Country: {{ billToCountry }}</div>
                </div>

                <table class="items">
                    <thead>
                        <tr>
                            <th style="width:40px;">#</th>
                            <th>Description</th>
                            <th class="num" style="width:70px;">Qty</th>
                            <th class="num" style="width:110px;">Unit Price</th>
                            <th class="num" style="width:120px;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>{{ lineDescription }}</td>
                            <td class="num">1</td>
                            <td class="num">{{ formatMoney(unitPrice) }}</td>
                            <td class="num">{{ formatMoney(unitPrice) }}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="totals">
                    <table>
                        <tr><td class="lbl">Subtotal</td><td class="amt">{{ formatMoney(unitPrice) }}</td></tr>
                        <tr class="discount"><td class="lbl">Less Discount</td><td class="amt">{{ formatMoney(0) }}</td></tr>
                        <tr><td class="lbl">Taxable Amount</td><td class="amt">{{ formatMoney(unitPrice) }}</td></tr>
                        <tr><td class="lbl">Tax (Zero-rated, GST 0%)</td><td class="amt">0.00</td></tr>
                        <tr class="grand"><td class="lbl">TOTAL DUE</td><td class="amt">{{ formatMoney(unitPrice) }}</td></tr>
                    </table>
                </div>

                <div class="bank">
                    <div class="lbl">Payment Instructions</div>
                    <div>Beneficiary: EXTRACURRICULAR EXCELLENCE INT PTE. LTD.</div>
                    <div>Bank: DBS Bank Ltd Singapore &nbsp; Account No.: 072-152241-1</div>
                    <div>SWIFT / BIC: DBSSSGSG</div>
                    <div>Reference: {{ invoiceNo }}</div>
                </div>

                <div class="note">
                    Please remit payment by the due date. Kindly quote the invoice number in your transfer reference.
                    This document is a commercial invoice and not a tax receipt; a receipt will be issued upon payment.
                </div>

                <div class="docfoot">EXTRACURRICULAR ACTIVITY EXCELLENCE INTERNATIONAL PTE. LTD. &nbsp;·&nbsp; UEN 202405060C</div>
            </section>

            <!-- ============ RECEIPT ============ -->
            <section class="sheet" v-else>
                <div class="top">
                    <div class="seller">
                        <div class="name">EXTRACURRICULAR ACTIVITY EXCELLENCE INTERNATIONAL PTE. LTD.</div>
                        <div>60 PAYA LEBAR ROAD, #06-28 PAYA LEBAR SQUARE, SINGAPORE 409051</div>
                        <div>UEN: 202405060C</div>
                    </div>
                    <div class="meta">
                        <table>
                            <tr><td class="k">RECEIPT NO.</td><td class="v">{{ receiptNo }}</td></tr>
                            <tr><td class="k">DATE OF PAYMENT</td><td class="v">{{ paymentDate }}</td></tr>
                            <tr><td class="k">CURRENCY</td><td class="v">{{ currency }}</td></tr>
                        </table>
                    </div>
                </div>

                <p style="font-size:15px;margin:4px 0 18px;">
                    Received from <b>{{ billToName }}</b> the sum of
                    <span class="recv-amt">{{ formatMoney(amountReceived) }}</span>
                    in settlement of <b>Invoice No. {{ invoiceNo }}</b> dated {{ issueDate }}.
                </p>

                <div class="totals" style="width:380px;">
                    <table>
                        <tr><td class="lbl">Invoice Total</td><td class="amt">{{ formatMoney(unitPrice) }}</td></tr>
                        <tr class="discount"><td class="lbl">Less Discount Applied</td><td class="amt">{{ formatMoney(0) }}</td></tr>
                        <tr><td class="lbl">Amount Due (net)</td><td class="amt">{{ formatMoney(unitPrice) }}</td></tr>
                        <tr><td class="lbl">Amount Received</td><td class="amt">{{ formatMoney(amountReceived) }}</td></tr>
                        <tr :class="['grand', balanceDue > 0 ? 'balance-warn' : '']">
                            <td class="lbl">Balance Due</td>
                            <td class="amt">{{ formatMoney(balanceDue) }}</td>
                        </tr>
                    </table>
                </div>

                <div class="payinfo">
                    <div class="payrow"><span class="pl">Payment Method</span><span class="pv">{{ paymentMethod }}</span></div>
                    <div class="payrow"><span class="pl">Transaction ID</span><span class="pv">{{ transactionId }}</span></div>
                </div>

                <div class="note">
                    <span v-if="balanceDue > 0" style="color:#b91c1c;">
                        If Balance Due &gt; 0, this receipt confirms partial payment only; the remaining balance remains payable.
                    </span>
                    <span v-else>
                        This receipt confirms full settlement and discharge of the invoice.
                    </span>
                </div>

                <div class="docfoot">EXTRACURRICULAR ACTIVITY EXCELLENCE INTERNATIONAL PTE. LTD. &nbsp;·&nbsp; UEN 202405060C</div>
            </section>
        </div>
    </div>
</template>

<script>
import { checkApplication } from '../../api/eae'
import { formatDate } from '../../utils/date'

export default {
  name: 'PaymentPDF',
  data() {
    return {
      applicationInfo: null,
      id: '',
      documentMode: this.$route.query.document || 'invoice',
      loading: false
    }
  },
  computed: {
    currency() {
      const p = this.applicationInfo && this.applicationInfo.project_info
      return (p && p.project_price_currency) || 'USD'
    },
    invoiceNo() {
      const id = this.id || ''
      return id ? ('INV-' + id.slice(-8).toUpperCase()) : 'INV-PENDING'
    },
    receiptNo() {
      const id = this.id || ''
      return id ? ('RCP-' + id.slice(-8).toUpperCase()) : 'RCP-PENDING'
    },
    issueDate() {
      if (!this.applicationInfo) return ''
      return this.formatUnixDate(this.applicationInfo.create_time)
    },
    dueDate() {
      const p = this.applicationInfo && this.applicationInfo.project_info
      if (!p) return ''
      const raw = p.deadline_date || p.project_deadline || p.registration_deadline
      if (!raw) return this.issueDate
      return this.formatAnyDate(raw)
    },
    paymentDate() {
      if (!this.applicationInfo) return ''
      if (this.applicationInfo.application_payment_date) {
        return String(this.applicationInfo.application_payment_date)
      }
      if (this.applicationInfo.institution_payment_verified_time) {
        return this.formatUnixDate(this.applicationInfo.institution_payment_verified_time)
      }
      if (this.applicationInfo.application_payment_time) {
        return this.formatUnixDate(this.applicationInfo.application_payment_time)
      }
      return ''
    },
    billToName() {
      if (!this.applicationInfo) return ''
      return this.applicationInfo.account_name
        || this.applicationInfo.account_nameEN
        || this.applicationInfo.student_name
        || '—'
    },
    billToCountry() {
      if (!this.applicationInfo) return ''
      return this.applicationInfo.account_country
        || (this.applicationInfo.account_info && this.applicationInfo.account_info.account_country)
        || ''
    },
    lineDescription() {
      const p = this.applicationInfo && this.applicationInfo.project_info
      const name = (p && (p.project_ename || p.project_name)) || 'Program licence fee'
      const isInstitution = this.applicationInfo && this.applicationInfo.institution_payment_enabled === 'YES'
      return isInstitution
        ? ('Institution licence fee — ' + name)
        : ('Registration fee — ' + name)
    },
    unitPrice() {
      if (!this.applicationInfo) return 0
      const actual = Number(this.applicationInfo.institution_payment_actual_amount)
      if (this.documentMode === 'receipt' && actual > 0) {
        // Receipt unit/invoice total still uses order net; amount received uses actual
      }
      return Number(
        this.applicationInfo.total_price
        || this.applicationInfo.institution_price
        || 0
      )
    },
    amountReceived() {
      if (!this.applicationInfo) return 0
      const paid = Number(
        this.applicationInfo.application_payment_amount
        || this.applicationInfo.institution_payment_actual_amount
        || 0
      )
      if (paid > 0) return paid
      if (this.applicationInfo.application_status === '已缴费') {
        return this.unitPrice
      }
      return 0
    },
    balanceDue() {
      return Math.max(0, Number((this.unitPrice - this.amountReceived).toFixed(2)))
    },
    paymentMethod() {
      if (!this.applicationInfo) return 'N/A'
      return this.applicationInfo.application_payment_channel
        || this.applicationInfo.payment_method
        || 'N/A'
    },
    transactionId() {
      if (!this.applicationInfo) return 'N/A'
      const method = String(this.paymentMethod || '').toLowerCase()
      if (method.indexOf('stripe') !== -1 || method.indexOf('card') !== -1) {
        return this.applicationInfo.application_paymentintent_id
          || this.applicationInfo.stripe_payment_intent_id
          || this.applicationInfo.checkout_session_id
          || 'N/A'
      }
      return 'N/A'
    }
  },
  mounted() {
    this.id = this.$route.query.id
    this.fetchData()
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({ path: '/home/studentRegistrations' })
      }
    },
    fetchData() {
      if (!this.id) {
        this.$message.warning('Missing application id')
        return
      }
      this.loading = true
      checkApplication(this.id).then((res) => {
        this.applicationInfo = res.data.data
      }).catch(() => {
        this.$message.error('Failed to load invoice / receipt data')
      }).finally(() => {
        this.loading = false
      })
    },
    formatMoney(amount) {
      return Number(amount || 0).toFixed(2)
    },
    formatUnixDate(timestamp) {
      if (!timestamp) return ''
      const n = Number(timestamp)
      if (!n) return ''
      const ms = String(timestamp).length > 10 ? n : n * 1000
      return formatDate(new Date(ms), 'yyyy-MM-dd')
    },
    formatAnyDate(value) {
      if (!value) return ''
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        return this.formatUnixDate(value)
      }
      const d = new Date(value)
      if (isNaN(d.getTime())) return String(value).slice(0, 10)
      return formatDate(d, 'yyyy-MM-dd')
    },
    exportPDF() {
      const el = document.getElementById('invoiceReceiptPrint')
      if (!el) {
        this.$message.warning('Document is still loading.')
        return
      }
      const style = document.createElement('style')
      style.innerHTML = '@page{size:A4 portrait;margin:12mm;} .no-print{display:none!important;}'
      window.document.head.appendChild(style)
      const printData = el.innerHTML
      const original = window.document.body.innerHTML
      window.document.body.innerHTML = '<div class="wrap" style="max-width:860px;margin:0 auto;font-family:Segoe UI,Arial,Helvetica Neue,sans-serif;color:#1f2937;">' + printData + '</div>'
      window.print()
      window.document.body.innerHTML = original
      window.location.reload()
    }
  }
}
</script>

<style lang="scss" scoped>
.doc-page {
  background: #f7f8fa;
  min-height: 100vh;
  padding: 20px 16px 40px;
  box-sizing: border-box;
}
.doc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto 16px;
  max-width: 860px;
}
.wrap {
  max-width: 860px;
  margin: 0 auto;
}
.sheet {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 40px 44px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin: 0 0 28px;
}
.seller {
  max-width: 55%;
}
.seller .name {
  font-weight: 700;
  font-size: 17px;
  color: #24407f;
}
.seller div {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}
.meta {
  font-size: 13px;
  text-align: right;
  min-width: 240px;
}
.meta table {
  width: 100%;
  border-collapse: collapse;
}
.meta td {
  padding: 2px 0;
  vertical-align: top;
}
.meta td.k {
  color: #6b7280;
  text-align: right;
  padding-right: 10px;
  white-space: nowrap;
}
.meta td.v {
  font-weight: 600;
  text-align: right;
  color: #1f2937;
}
.billto {
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  margin-bottom: 24px;
}
.billto .lbl {
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
table.items {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 20px;
}
table.items thead th {
  background: #2f509f;
  color: #fff;
  text-align: left;
  padding: 9px 10px;
  font-weight: 600;
  font-size: 12px;
}
table.items thead th.num,
table.items tbody td.num {
  text-align: right;
}
table.items tbody td {
  border-bottom: 1px solid #e5e7eb;
  padding: 9px 10px;
}
.totals {
  margin-left: auto;
  width: 320px;
  font-size: 13px;
}
.totals table {
  width: 100%;
  border-collapse: collapse;
}
.totals td {
  padding: 5px 0;
}
.totals td.lbl {
  color: #6b7280;
}
.totals td.amt {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.totals tr.discount td.amt {
  color: #b45309;
}
.totals tr.grand td {
  border-top: 2px solid #2f509f;
  font-size: 15px;
  font-weight: 700;
  color: #2f509f;
}
.totals tr.balance-warn td {
  color: #b91c1c;
}
.bank {
  background: #f7f8fa;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #f3a600;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  margin-top: 8px;
  color: #1f2937;
}
.bank .lbl {
  font-weight: 700;
  color: #24407f;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 14px;
  line-height: 1.5;
}
.docfoot {
  margin-top: 28px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  letter-spacing: 0.3px;
}
.payinfo {
  margin-top: 18px;
  font-size: 13px;
}
.payrow {
  display: flex;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px dotted #e5e7eb;
}
.payrow .pl {
  width: 150px;
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-top: 2px;
}
.payrow .pv {
  font-weight: 600;
  color: #1f2937;
}
.recv-amt {
  font-size: 26px;
  font-weight: 700;
  color: #2f509f;
}
@media print {
  .doc-page {
    background: #fff;
    padding: 0;
  }
  .sheet {
    box-shadow: none;
    border: none;
  }
}
</style>
